import { useEffect, useRef, useState } from 'react';
import QrScanner from 'qr-scanner';
import QrScannerWorkerSrc from 'qr-scanner/qr-scanner-worker.min.js?url';

QrScanner.WORKER_PATH = QrScannerWorkerSrc;

interface CameraScannerProps {
  active: boolean;
  onDecode: (text: string) => void;
}

/** Ported from apps/scanner/src/components/CameraScanner.tsx (identical behaviour) - the owner
 * wants ticket scanning available INSIDE this app rather than requiring a separate one, so the
 * camera-decode component moves here too instead of staying scanner-app-only. */
export function CameraScanner({ active, onDecode }: CameraScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<QrScanner | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const scanner = new QrScanner(video, (result) => onDecode(result.data), {
      highlightScanRegion: true,
      highlightCodeOutline: true,
      maxScansPerSecond: 5,
      returnDetailedScanResult: true,
    });
    scannerRef.current = scanner;

    return () => {
      scanner.destroy();
      scannerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onDecode is passed fresh each
    // render by the caller; re-creating the scanner on every decode would restart the camera.
  }, []);

  useEffect(() => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    if (active) {
      scanner
        .start()
        .then(() => setCameraError(null))
        .catch((err: unknown) =>
          setCameraError(
            err instanceof Error
              ? `Could not start the camera: ${err.message}. Check camera permission for this site.`
              : 'Could not start the camera for an unknown reason.',
          ),
        );
    } else {
      scanner.stop();
    }
  }, [active]);

  return (
    <div className="relative aspect-square w-full max-w-sm overflow-hidden rounded-xl bg-black">
      <video ref={videoRef} className="h-full w-full object-cover" muted playsInline />
      {cameraError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-4 text-center text-sm text-red-300">
          {cameraError}
        </div>
      )}
    </div>
  );
}
